import type { GameMode, PieceType, Point, Rotation } from "./types";
import { GAME } from "../config/gameConfig";

export const PIECE_TYPES: readonly PieceType[] = ["DOMINO", "TRI_BAR", "TRI_CORNER", "TRI_CORNER_MIRROR", "CROSS", "CUP"];
export const ALTERNATE_PIECE_TYPES: readonly PieceType[] = ["I", "O", "T", "S", "Z", "J", "L"];

function rotations(cells: readonly Point[]): readonly (readonly Point[])[] {
  const result: (readonly Point[])[] = [cells];
  for (let i = 1; i < 4; i++) result.push(result[i - 1]!.map(({ x, y }) => ({ x: 2 - y, y: x })));
  return result;
}

const SHAPES: Record<PieceType, readonly (readonly Point[])[]> = {
  DOMINO: rotations([{ x: 1, y: 1 }, { x: 2, y: 1 }]),
  TRI_BAR: rotations([{ x: 0, y: 1 }, { x: 1, y: 1 }, { x: 2, y: 1 }]),
  TRI_CORNER: rotations([{ x: 1, y: 0 }, { x: 1, y: 1 }, { x: 2, y: 1 }]),
  TRI_CORNER_MIRROR: rotations([{ x: 1, y: 0 }, { x: 1, y: 1 }, { x: 0, y: 1 }]),
  CROSS: rotations([{ x: 1, y: 0 }, { x: 0, y: 1 }, { x: 1, y: 1 }, { x: 2, y: 1 }, { x: 1, y: 2 }]),
  CUP: rotations([{ x: 0, y: 0 }, { x: 2, y: 0 }, { x: 0, y: 1 }, { x: 1, y: 1 }, { x: 2, y: 1 }]),
  B1: Array.from({ length: 4 }, () => [{ x: 1, y: 1 }]),
  B2: [
    [{ x: 1, y: 0 }, { x: 1, y: 1 }],
    [{ x: 1, y: 1 }, { x: 2, y: 1 }],
    [{ x: 1, y: 1 }, { x: 1, y: 2 }],
    [{ x: 0, y: 1 }, { x: 1, y: 1 }],
  ],
  I: [
    [{ x: 0, y: 1 }, { x: 1, y: 1 }, { x: 2, y: 1 }, { x: 3, y: 1 }],
    [{ x: 2, y: 0 }, { x: 2, y: 1 }, { x: 2, y: 2 }, { x: 2, y: 3 }],
    [{ x: 0, y: 2 }, { x: 1, y: 2 }, { x: 2, y: 2 }, { x: 3, y: 2 }],
    [{ x: 1, y: 0 }, { x: 1, y: 1 }, { x: 1, y: 2 }, { x: 1, y: 3 }],
  ],
  O: Array.from({ length: 4 }, () => [
    { x: 1, y: 0 }, { x: 2, y: 0 }, { x: 1, y: 1 }, { x: 2, y: 1 },
  ]),
  T: [
    [{ x: 1, y: 0 }, { x: 0, y: 1 }, { x: 1, y: 1 }, { x: 2, y: 1 }],
    [{ x: 1, y: 0 }, { x: 1, y: 1 }, { x: 2, y: 1 }, { x: 1, y: 2 }],
    [{ x: 0, y: 1 }, { x: 1, y: 1 }, { x: 2, y: 1 }, { x: 1, y: 2 }],
    [{ x: 1, y: 0 }, { x: 0, y: 1 }, { x: 1, y: 1 }, { x: 1, y: 2 }],
  ],
  S: [
    [{ x: 1, y: 0 }, { x: 2, y: 0 }, { x: 0, y: 1 }, { x: 1, y: 1 }],
    [{ x: 1, y: 0 }, { x: 1, y: 1 }, { x: 2, y: 1 }, { x: 2, y: 2 }],
    [{ x: 1, y: 1 }, { x: 2, y: 1 }, { x: 0, y: 2 }, { x: 1, y: 2 }],
    [{ x: 0, y: 0 }, { x: 0, y: 1 }, { x: 1, y: 1 }, { x: 1, y: 2 }],
  ],
  Z: [
    [{ x: 0, y: 0 }, { x: 1, y: 0 }, { x: 1, y: 1 }, { x: 2, y: 1 }],
    [{ x: 2, y: 0 }, { x: 1, y: 1 }, { x: 2, y: 1 }, { x: 1, y: 2 }],
    [{ x: 0, y: 1 }, { x: 1, y: 1 }, { x: 1, y: 2 }, { x: 2, y: 2 }],
    [{ x: 1, y: 0 }, { x: 0, y: 1 }, { x: 1, y: 1 }, { x: 0, y: 2 }],
  ],
  J: [
    [{ x: 0, y: 0 }, { x: 0, y: 1 }, { x: 1, y: 1 }, { x: 2, y: 1 }],
    [{ x: 1, y: 0 }, { x: 2, y: 0 }, { x: 1, y: 1 }, { x: 1, y: 2 }],
    [{ x: 0, y: 1 }, { x: 1, y: 1 }, { x: 2, y: 1 }, { x: 2, y: 2 }],
    [{ x: 1, y: 0 }, { x: 1, y: 1 }, { x: 0, y: 2 }, { x: 1, y: 2 }],
  ],
  L: [
    [{ x: 2, y: 0 }, { x: 0, y: 1 }, { x: 1, y: 1 }, { x: 2, y: 1 }],
    [{ x: 1, y: 0 }, { x: 1, y: 1 }, { x: 1, y: 2 }, { x: 2, y: 2 }],
    [{ x: 0, y: 1 }, { x: 1, y: 1 }, { x: 2, y: 1 }, { x: 0, y: 2 }],
    [{ x: 0, y: 0 }, { x: 1, y: 0 }, { x: 1, y: 1 }, { x: 1, y: 2 }],
  ],
};

export function cellsFor(type: PieceType, rotation: Rotation): readonly Point[] {
  return SHAPES[type][rotation] ?? SHAPES[type][0]!;
}

export function isSpanning(type: PieceType): boolean { return type === "B1" || type === "B2"; }

export const MAX_PIECE_CELLS = Math.max(...Object.values(SHAPES).map(states => states[0]!.length));

export class PieceBag {
  private queue: PieceType[] = [];
  constructor(private readonly random: () => number = Math.random, private mode: GameMode = "standard") {}

  next(): PieceType {
    // Insert a shared piece without consuming the mode's ordinary piece bag.
    if (this.random() < GAME.spanningChance) return this.random() < 0.5 ? "B1" : "B2";
    if (this.queue.length === 0) this.refill();
    return this.queue.shift()!;
  }

  reset(mode: GameMode = this.mode): void {
    this.mode = mode;
    this.queue.length = 0;
  }

  private refill(): void {
    this.queue = [...(this.mode === "alternate" ? ALTERNATE_PIECE_TYPES : PIECE_TYPES)];
    for (let i = this.queue.length - 1; i > 0; i -= 1) {
      const j = Math.floor(this.random() * (i + 1));
      [this.queue[i], this.queue[j]] = [this.queue[j]!, this.queue[i]!];
    }
  }
}
